import { DateTime } from 'luxon';

/** A calendar day represented as YYYY-MM-DD (no timezone information). */
export type CalendarDate = string;

/** Returns the IANA timezone identifier of the browser. */
export function browserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** Returns today's CalendarDate in the given IANA timezone. */
export function todayIn(tz: string): CalendarDate {
    return DateTime.now().setZone(tz).toISODate()!;
}

/**
 * Converts a CalendarDate to a native Date at local midnight.
 * Use this when you need a Date object for the PrimeVue DatePicker.
 */
export function calendarToJSDate(cal: CalendarDate): Date {
    const [y, m, d] = cal.split('-').map(Number);
    return new Date(y, m - 1, d);
}

/**
 * Converts a native Date (from a picker, at local midnight) to a CalendarDate YYYY-MM-DD string.
 * Simply reads the local year/month/day — no timezone shift.
 */
export function jsDateToCalendar(date: Date): CalendarDate {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

/** Start of the given unit in the specified timezone, from the given CalendarDate (defaults to today in tz). */
export function startOf(
    unit: 'day' | 'week' | 'month' | 'quarter' | 'year',
    tz: string,
    from?: CalendarDate,
): CalendarDate {
    const base = from
        ? DateTime.fromISO(from, { zone: tz })
        : DateTime.now().setZone(tz);
    return base.startOf(unit).toISODate()!;
}

/** End of the given unit in the specified timezone, from the given CalendarDate (defaults to today in tz). */
export function endOf(
    unit: 'day' | 'week' | 'month' | 'quarter' | 'year',
    tz: string,
    from?: CalendarDate,
): CalendarDate {
    const base = from
        ? DateTime.fromISO(from, { zone: tz })
        : DateTime.now().setZone(tz);
    return base.endOf(unit).toISODate()!;
}

/** Adds n days to a CalendarDate. */
export function addDays(cal: CalendarDate, n: number): CalendarDate {
    return DateTime.fromISO(cal).plus({ days: n }).toISODate()!;
}

/** Adds n weeks (7-day steps) to a CalendarDate. */
export function addWeeks(cal: CalendarDate, n: number): CalendarDate {
    return DateTime.fromISO(cal).plus({ weeks: n }).toISODate()!;
}

/** Adds n calendar months to a CalendarDate (Luxon clamps month-end overflow, e.g. Jan 31 + 1 → Feb 28). */
export function addMonths(cal: CalendarDate, n: number): CalendarDate {
    return DateTime.fromISO(cal).plus({ months: n }).toISODate()!;
}

/** Number of whole days from one CalendarDate to another (to − from). */
export function diffDays(from: CalendarDate, to: CalendarDate): number {
    return Math.round(DateTime.fromISO(to).diff(DateTime.fromISO(from), 'days').days);
}

/** Number of whole months from one CalendarDate to another. */
export function diffMonths(from: CalendarDate, to: CalendarDate): number {
    return Math.round(
        DateTime.fromISO(to, { zone: 'UTC' })
            .diff(DateTime.fromISO(from, { zone: 'UTC' }), 'months').months,
    );
}

export function isSameDay(a: CalendarDate, b: CalendarDate): boolean {
    return a === b;
}

export function isBefore(a: CalendarDate, b: CalendarDate): boolean {
    return a < b;
}

/** Formats a CalendarDate (YYYY-MM-DD) or ISO timestamp as MM/DD/YYYY. */
export function formatDisplayDate(val: string): string {
    if (!val) return '';
    return DateTime.fromISO(val).toFormat('MM/dd/yyyy');
}

/** Parses a display date string (MM/DD/YYYY) into a CalendarDate (YYYY-MM-DD). */
export function parseDisplayDate(display: string): CalendarDate {
    const [m, d, y] = display.split('/');
    return `${y}-${m}-${d}`;
}

/**
 * Formats a Date (UTC instant) as 'MM/DD/YYYY at hh:mma' in the given timezone.
 * Use for notification timestamps and other absolute-time displays.
 */
export interface FormatTimestampOptions {
    padHour?: boolean;
}

export function formatTimestamp(
    date: Date,
    tz: string,
    options: FormatTimestampOptions = {},
): string {
    const hourToken = options.padHour === false ? 'h' : 'hh';
    return DateTime
        .fromJSDate(date)
        .setZone(tz)
        .toFormat(`MM/dd/yyyy 'at' ${hourToken}:mma`)
        .toLowerCase();
}

/** Formats a CalendarDate for use as a chart axis label: MM/dd for days/weeks, MMM for months. */
export function formatChartLabel(cal: CalendarDate, viewMode: 'days' | 'weeks' | 'months'): string {
    const dt = DateTime.fromISO(cal);
    return viewMode === 'months' ? dt.toFormat('MMM') : dt.toFormat('MM/dd');
}
